import { useEffect, useState } from "react";
import { hasRole, isRestricted } from "@/lib/roles";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmProvider";
import { ArrowLeft, Pencil, Trash2, Mail, Phone, MapPin, Calendar, Briefcase, DollarSign, ShieldCheck, Landmark, Eye, EyeOff, GraduationCap, Layers, Building, Award, CalendarClock, TrendingUp, X, Printer, Receipt, FileDown, FileText, Camera, CheckCircle2, Repeat, Plus, Power, PowerOff, IdCard } from "lucide-react";
import EmployeeDialog from "@/components/EmployeeDialog";
import PayChangeRequestDialog from "@/components/PayChangeRequestDialog";
import EmployeeDocuments from "@/components/EmployeeDocuments";
import FaceCapture from "@/components/FaceCapture";
import PassportPhoto from "@/components/PassportPhoto";
import PassportPhotoUploader from "@/components/PassportPhotoUploader";
import { MoneyInput } from "@/components/MoneyInput";

function InfoCell({ label, value, icon: Icon, testid, mono = false }) {
    return (
        <div className="p-6 border-r border-b border-ink/10 last:border-r-0" data-testid={testid}>
            <div className="overline flex items-center gap-2">
                {Icon && <Icon className="w-3 h-3" />} {label}
            </div>
            <div className={`mt-2 text-ink text-[15px] leading-snug break-words ${mono ? "font-mono tracking-wide" : ""}`}>
                {value || <span className="text-ink/30">—</span>}
            </div>
        </div>
    );
}

function MaskedCell({ label, value, icon: Icon, testid, show, onToggle }) {
    const masked = value ? value.replace(/.(?=.{4})/g, "•") : "";
    return (
        <div className="p-6 border-r border-b border-ink/10 last:border-r-0" data-testid={testid}>
            <div className="flex items-center justify-between gap-2">
                <div className="overline flex items-center gap-2">
                    {Icon && <Icon className="w-3 h-3" />} {label}
                </div>
                {value && (
                    <button
                        type="button"
                        onClick={onToggle}
                        className="text-ink/40 hover:text-klein p-1 -m-1"
                        aria-label={show ? "Hide" : "Show"}
                        data-testid={`${testid}-toggle`}
                    >
                        {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                )}
            </div>
            <div className="mt-2 text-ink text-[15px] leading-snug break-words font-mono tracking-wide">
                {value ? (show ? value : masked) : <span className="text-ink/30 font-sans">—</span>}
            </div>
        </div>
    );
}

export default function EmployeeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isEmployee = user?.role === "employee";
    const noPay = isRestricted(user);
    const canManageFace = hasRole(user, "admin", "hr");
    const { confirm } = useConfirm();
    const [faceStatus, setFaceStatus] = useState(null);
    const [faceOpen, setFaceOpen] = useState(false);
    const [faceSaving, setFaceSaving] = useState(false);
    const [employee, setEmployee] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [balance, setBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [passportOpen, setPassportOpen] = useState(false);
    const [reveal, setReveal] = useState({ bvn: false, nin: false, tax_id: false, pension_id: false, bank_account_number: false });
    const toggleReveal = (k) => setReveal((r) => ({ ...r, [k]: !r[k] }));
    const [incOpen, setIncOpen] = useState(false);
    const [pcrOpen, setPcrOpen] = useState(false);
    const [incForm, setIncForm] = useState({ new_salary: 0, effective_date: "", reason: "" });
    const [incSaving, setIncSaving] = useState(false);

    const [ffOpen, setFfOpen] = useState(false);
    const [ffKind, setFfKind] = useState("resignation");
    const [ffEnd, setFfEnd] = useState("");
    const [ffNote, setFfNote] = useState("");
    const [ffLoading, setFfLoading] = useState(false);
    const [ffData, setFfData] = useState(null);
    const [ffSubmitting, setFfSubmitting] = useState(false);

    // Letter generator
    const [letterOpen, setLetterOpen] = useState(false);
    const [letterTemplates, setLetterTemplates] = useState([]);
    const [letterTemplateId, setLetterTemplateId] = useState("");
    const [letterExtra, setLetterExtra] = useState("");
    const [letterDate, setLetterDate] = useState(new Date().toISOString().slice(0, 10));
    const [letterSalary, setLetterSalary] = useState("");
    const [letterDownloading, setLetterDownloading] = useState(false);

    const openLetter = async () => {
        try {
            const { data } = await api.get("/letter-templates");
            setLetterTemplates(data);
            setLetterTemplateId(data[0]?.id || "");
            setLetterExtra("");
            setLetterDate(new Date().toISOString().slice(0, 10));
            setLetterSalary("");
            setLetterOpen(true);
        } catch (err) {
            toast.error("Could not load letter templates");
        }
    };

    const downloadLetter = async () => {
        if (!letterTemplateId) { toast.error("Pick a template"); return; }
        setLetterDownloading(true);
        try {
            const overrides = { date: letterDate };
            if (letterSalary) overrides.salary = letterSalary;
            const res = await api.post(`/employees/${id}/letter`, {
                template_id: letterTemplateId,
                overrides,
                extra_body: letterExtra || "",
            }, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            const safeName = `${employee.first_name || ""}_${employee.last_name || ""}`.replace(/\s+/g, "_");
            const tplKey = letterTemplates.find((t) => t.id === letterTemplateId)?.key || "letter";
            a.download = `letter_${tplKey}_${safeName}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Letter downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Letter generation failed");
        } finally {
            setLetterDownloading(false);
        }
    };

    const openSettlement = () => {
        const isTerm = (employee?.status === "terminated");
        const kind = isTerm ? "termination" : "resignation";
        const end =
            (isTerm ? employee?.termination_date : employee?.resignation_date) ||
            new Date().toISOString().slice(0, 10);
        setFfKind(kind);
        setFfEnd(end);
        setFfNote("");
        setFfData(null);
        setFfOpen(true);
        loadSettlement(kind, end);
    };

    const loadSettlement = async (kind, end) => {
        setFfLoading(true);
        try {
            const { data } = await api.get(`/employees/${id}/settlement`, {
                params: { kind, end_date: end || undefined },
            });
            setFfData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not compute settlement");
        } finally {
            setFfLoading(false);
        }
    };

    const submitSettlement = async () => {
        if (!ffEnd) { toast.error("Pick an effective date"); return; }
        const ok = await confirm({
            title: `Initiate ${ffKind} settlement?`,
            description: `For ${employee.first_name} ${employee.last_name}. The department lead will be notified by email.`,
            confirmLabel: `Initiate ${ffKind}`,
        });
        if (!ok) return;
        setFfSubmitting(true);
        try {
            const { data } = await api.post(`/settlements`, {
                employee_id: id, kind: ffKind, end_date: ffEnd, note: ffNote,
            });
            toast.success("Settlement initiated. Department lead will review next.");
            setFfOpen(false);
            navigate(`/settlements`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not initiate settlement");
        } finally {
            setFfSubmitting(false);
        }
    };

    const downloadSettlementPdf = async () => {
        try {
            const res = await api.get(`/employees/${id}/settlement/pdf`, {
                params: { kind: ffKind, end_date: ffEnd || undefined },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            const safeName = `${employee.first_name || ""}_${employee.last_name || ""}`.replace(/\s+/g, "_");
            a.download = `settlement_preview_${ffKind}_${safeName}_${ffEnd || "today"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Preview PDF downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "PDF download failed");
        }
    };

    const onSubmitIncrement = async (e) => {
        e.preventDefault();
        if (!Number(incForm.new_salary) || Number(incForm.new_salary) <= 0) {
            toast.error("Enter a valid new salary");
            return;
        }
        setIncSaving(true);
        try {
            await api.post(`/employees/${id}/salary-increment`, {
                new_salary: Number(incForm.new_salary),
                new_other_allowance: Math.max(0, Number(incForm.new_other_allowance) || 0),
                effective_date: incForm.effective_date || "",
                reason: incForm.reason,
            });
            toast.success("Salary change recorded");
            setIncOpen(false);
            setIncForm({ new_salary: 0, new_other_allowance: 0, effective_date: "", reason: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Increment failed");
        } finally {
            setIncSaving(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const [e, d, l, b] = await Promise.all([
                api.get(`/employees/${id}`),
                api.get("/departments"),
                api.get(`/leaves`, { params: { employee_id: id } }).catch(() => ({ data: [] })),
                api.get(`/employees/${id}/leave-balance`),
            ]);
            setEmployee(e.data);
            setDepartments(d.data);
            setLeaves(l.data);
            setBalance(b.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Employee not found");
            navigate("/employees");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [id]);

    // Load current face enrollment status (admin/HR + self).
    useEffect(() => {
        if (!id) return;
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get(`/employees/${id}/face/status`);
                if (alive) setFaceStatus(data);
            } catch {/* ignore */}
        })();
        return () => { alive = false; };
    }, [id]);

    const onFaceEnrolled = async (descriptors) => {
        setFaceSaving(true);
        try {
            await api.post(`/employees/${id}/face/enroll`, { face_descriptors: descriptors });
            toast.success("Face re-enrollment saved");
            setFaceOpen(false);
            const { data } = await api.get(`/employees/${id}/face/status`);
            setFaceStatus(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Re-enrollment failed");
        } finally {
            setFaceSaving(false);
        }
    };

    const onDelete = async () => {
        const ok = await confirm({
            title: `Delete ${employee.first_name} ${employee.last_name}?`,
            description: "This will permanently remove the employee record, all related documents, payroll history, and their linked account. This cannot be undone.",
            confirmLabel: "Delete employee",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/employees/${id}`);
            toast.success("Employee deleted");
            navigate("/employees");
        } catch (e) {
            toast.error("Failed to delete");
        }
    };

    const onDownloadIdCard = async () => {
        try {
            const res = await api.get(`/employees/${id}/id-card.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `id-card-${employee.employee_no || id}.pdf`;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            toast.success("ID card downloaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not download ID card");
        }
    };

    if (loading || !employee) {
        return (
            <div className="px-8 md:px-12 py-12 text-sm text-ink/40">Loading…</div>
        );
    }

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="employee-detail-page">
            {!isEmployee && (
            <Link
                to="/employees"
                className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-klein mb-8"
                data-testid="back-to-employees"
            >
                <ArrowLeft className="w-4 h-4" /> All employees
            </Link>
            )}
            {isEmployee && (
            <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-klein mb-8"
                data-testid="back-to-dashboard"
            >
                <ArrowLeft className="w-4 h-4" /> Back to dashboard
            </Link>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
                {/* Photo & identity */}
                <div className="bg-white p-10">
                    <div className="relative aspect-square bg-ink/5 overflow-hidden group">
                        <PassportPhoto
                            employee={employee}
                            className="w-full h-full object-cover"
                            initialsClassName="w-full h-full text-7xl"
                        />
                        <button
                            type="button"
                            onClick={() => setPassportOpen(true)}
                            className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-klein text-white px-3 py-2 text-xs font-medium shadow hover:bg-klein/90 opacity-0 group-hover:opacity-100 transition"
                            data-testid="passport-open-button"
                        >
                            <Camera className="w-3.5 h-3.5" /> {employee.passport_photo_path ? "Change" : "Add photo"}
                        </button>
                    </div>
                    <div className="mt-6">
                        <div className="overline">{employee.employment_type}</div>
                        <h1 className="mt-2 font-display font-black tracking-tighter text-3xl text-ink leading-tight">
                            {employee.full_name || [employee.last_name, employee.first_name, employee.other_name].filter(Boolean).join(" ") || "—"}
                        </h1>
                        <div className="mt-2 text-ink/70 text-[15px]">{employee.position || "—"}</div>
                        <div className="mt-1 text-ink/50 text-sm">{employee.department || "—"}</div>
                    </div>

                    <div className="mt-8 flex items-center gap-2 print:hidden">
                        {!isEmployee && (
                        <button
                            onClick={() => setDialogOpen(true)}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-klein text-white px-4 py-2.5 text-sm hover:bg-klein/90"
                            data-testid="edit-employee-button"
                        >
                            <Pencil className="w-4 h-4" /> Edit
                        </button>
                        )}
                        {!isEmployee && (
                        <button
                            onClick={() => window.print()}
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink px-4 py-2.5 text-sm hover:bg-ink hover:text-white"
                            data-testid="print-employee-button"
                            title="Print employee record"
                        >
                            <Printer className="w-4 h-4" />
                        </button>
                        )}
                        {hasRole(user, "admin") && (
                        <button
                            onClick={() => { setIncForm({ new_salary: employee.salary || 0, new_other_allowance: employee.other_allowance || 0, effective_date: new Date().toISOString().slice(0, 10), reason: "" }); setIncOpen(true); }}
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink px-4 py-2.5 text-sm hover:bg-ink hover:text-white"
                            data-testid="salary-increment-button"
                            title="Change salary / other allowance (Admin only)"
                        >
                            <TrendingUp className="w-4 h-4" />
                        </button>
                        )}
                        {!hasRole(user, "admin") && hasRole(user, "hr") && (
                        <button
                            onClick={() => setPcrOpen(true)}
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink px-4 py-2.5 text-sm hover:bg-ink hover:text-white"
                            data-testid="request-pay-change-button"
                            title="Request a salary / allowance change (needs Admin approval)"
                        >
                            <TrendingUp className="w-4 h-4" />
                            <span className="sr-only">Request pay change</span>
                        </button>
                        )}
                        {!isEmployee && (employee.status === "resigned" || employee.status === "terminated" || employee.status === "active" || employee.status === "on_leave") && (
                            <button
                                onClick={onDownloadIdCard}
                                className="inline-flex items-center gap-2 border border-ink/20 text-ink/70 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                                data-testid="id-card-button"
                                title="Download employee ID card PDF"
                            >
                                <IdCard className="w-4 h-4" />
                            </button>
                        )}
                        {!isEmployee && !noPay && (employee.status === "resigned" || employee.status === "terminated" || employee.status === "active" || employee.status === "on_leave") && (
                            <button
                                onClick={openLetter}
                                className="inline-flex items-center gap-2 border border-ink/20 text-ink/70 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                                data-testid="letter-button"
                                title="Generate company letter (offer, confirmation, increment, warning, NOC, reference)"
                            >
                                <FileText className="w-4 h-4" />
                            </button>
                        )}
                        {!isEmployee && !noPay && (employee.status === "resigned" || employee.status === "terminated" || employee.status === "active" || employee.status === "on_leave") && (
                            <button
                                onClick={openSettlement}
                                className="inline-flex items-center gap-2 border border-klein text-klein px-4 py-2.5 text-sm hover:bg-klein hover:text-white"
                                data-testid="settlement-button"
                                title="Initiate Full & Final settlement (3-stage approval)"
                            >
                                <Receipt className="w-4 h-4" />
                            </button>
                        )}
                        {!isEmployee && (
                        <button
                            onClick={onDelete}
                            className="inline-flex items-center gap-2 border border-ink/20 text-ink px-4 py-2.5 text-sm hover:bg-destructive hover:text-white hover:border-destructive"
                            data-testid="delete-employee-button"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                        )}
                    </div>

                    {/* Face ID enrollment — Admin/HR only */}
                    {canManageFace && (
                        <div className="mt-6 border border-ink/10 p-4 print:hidden" data-testid="face-id-panel">
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="overline">Identity</div>
                                    <div className="mt-1 font-display font-bold text-sm text-ink flex items-center gap-2">
                                        <Camera className="w-4 h-4 text-klein" /> Face ID
                                    </div>
                                </div>
                                {faceStatus?.enrolled ? (
                                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1" data-testid="face-id-enrolled">
                                        <CheckCircle2 className="w-3 h-3" /> Enrolled
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-yellow-800 bg-yellow-50 border border-yellow-200" data-testid="face-id-not-enrolled">
                                        Not enrolled
                                    </span>
                                )}
                            </div>
                            <div className="mt-2 text-xs text-ink/55 leading-relaxed">
                                {faceStatus?.enrolled
                                    ? `${faceStatus.samples} sample${faceStatus.samples === 1 ? "" : "s"} on file. Re-enroll if the employee changed appearance (haircut, glasses, weight, etc.) or check-ins keep failing.`
                                    : "No face samples on record. Without enrollment, check-in/out runs in geo-only mode."}
                            </div>
                            <button
                                onClick={() => setFaceOpen(true)}
                                className="mt-3 inline-flex items-center gap-2 border border-klein text-klein px-3 py-2 text-xs hover:bg-klein hover:text-white"
                                data-testid="face-id-reenroll-btn"
                            >
                                <Camera className="w-3.5 h-3.5" /> {faceStatus?.enrolled ? "Re-enroll face" : "Enroll face"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Details */}
                <div className="lg:col-span-2 bg-white">
                    <div className="p-10 pb-6 border-b border-ink/10">
                        <div className="overline">Record</div>
                        <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink">
                            Profile details
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2">
                        <InfoCell label="Employee No" value={employee.employee_no} icon={Briefcase} testid="cell-employee-no" />
                        <InfoCell label="Email (personal)" value={employee.email} icon={Mail} testid="cell-email" />
                        <InfoCell label="Work email" value={employee.work_email} icon={Mail} testid="cell-work-email" />
                        <InfoCell label="Phone" value={employee.phone} icon={Phone} testid="cell-phone" />
                        <InfoCell label="Joining date" value={employee.joining_date} icon={Calendar} testid="cell-joining" />
                        <InfoCell label="Date of birth" value={employee.date_of_birth} icon={Calendar} testid="cell-dob" />
                        <InfoCell label="Position" value={employee.position} icon={Briefcase} testid="cell-position" />
                        <InfoCell label="Department" value={employee.department} icon={Briefcase} testid="cell-department" />
                        <InfoCell label="Division" value={employee.division} icon={Building} testid="cell-division" />
                        <InfoCell label="Category" value={employee.category} icon={Layers} testid="cell-category" />
                        <InfoCell label="Group" value={employee.group} icon={Layers} testid="cell-group" />
                        <InfoCell label="Cadre" value={employee.cadre} icon={Award} testid="cell-cadre" />
                        <InfoCell label="Qualification" value={employee.qualification} icon={GraduationCap} testid="cell-qualification" />
                        <InfoCell label="Course of study" value={employee.course_of_study} icon={GraduationCap} testid="cell-course-of-study" />
                        <InfoCell label="Additional qualification" value={employee.additional_qualification} icon={GraduationCap} testid="cell-additional-qualification" />
                        {!noPay && <InfoCell
                            label="Monthly salary"
                            value={employee.salary ? "₦" + Number(employee.salary).toLocaleString() : ""}
                            icon={DollarSign}
                            testid="cell-salary"
                        />}
                        {!noPay && <InfoCell
                            label="Other allowance (non-taxable)"
                            value={employee.other_allowance ? "₦" + Number(employee.other_allowance).toLocaleString() + " / month" : ""}
                            icon={DollarSign}
                            testid="cell-other-allowance"
                        />}
                        <InfoCell
                            label="Leave entitlement"
                            value={employee.leave_entitlement ? `${employee.leave_entitlement} days / year` : ""}
                            icon={CalendarClock}
                            testid="cell-leave-entitlement"
                        />
                        <InfoCell label="Gender" value={employee.gender} testid="cell-gender" />
                        <InfoCell label="Nationality" value={employee.nationality} testid="cell-nationality" />
                        <InfoCell
                            label="Address"
                            value={[employee.address, employee.state || employee.city, employee.country].filter(Boolean).join(", ")}
                            icon={MapPin}
                            testid="cell-address"
                        />
                        <InfoCell label="Status" value={employee.status} testid="cell-status" />
                    </div>

                    {employee.notes && (
                        <div className="p-10 border-t border-ink/10">
                            <div className="overline">Notes</div>
                            <p className="mt-3 text-ink/80 text-[15px] leading-relaxed whitespace-pre-wrap">
                                {employee.notes}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Compliance & Identity */}
            <div className="mt-12 bg-white border border-ink/10" data-testid="employee-compliance">
                <div className="p-10 pb-6 border-b border-ink/10 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="overline">Compliance & identity</div>
                        <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-3">
                            <ShieldCheck className="w-5 h-5 text-klein" strokeWidth={1.5} /> Statutory records
                        </h2>
                    </div>
                    <div className="text-xs text-ink/50 max-w-xs text-right">
                        Sensitive — masked by default. Toggle to reveal.
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <MaskedCell label="BVN" value={employee.bvn} testid="cell-bvn" show={reveal.bvn} onToggle={() => toggleReveal("bvn")} />
                    <MaskedCell label="NIN" value={employee.nin} testid="cell-nin" show={reveal.nin} onToggle={() => toggleReveal("nin")} />
                    <MaskedCell label="Taxpayer ID (TIN)" value={employee.tax_id} testid="cell-tax-id" show={reveal.tax_id} onToggle={() => toggleReveal("tax_id")} />
                    <MaskedCell label="Pension ID (RSA PIN)" value={employee.pension_id} testid="cell-pension-id" show={reveal.pension_id} onToggle={() => toggleReveal("pension_id")} />
                    <InfoCell label="Pension Fund Admin (PFA)" value={employee.pfa} testid="cell-pfa" />
                    <InfoCell label="Status" value={employee.status === "active" ? "Active — statutory compliant" : employee.status} testid="cell-compliance-status" />
                </div>
            </div>

            {/* Bank details */}
            <div className="mt-12 bg-white border border-ink/10" data-testid="employee-bank">
                <div className="p-10 pb-6 border-b border-ink/10">
                    <div className="overline">Payroll</div>
                    <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-3">
                        <Landmark className="w-5 h-5 text-klein" strokeWidth={1.5} /> Bank details
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3">
                    <InfoCell label="Bank" value={employee.bank_name} testid="cell-bank-name" />
                    <MaskedCell
                        label="Account number (NUBAN)"
                        value={employee.bank_account_number}
                        testid="cell-bank-account-number"
                        show={reveal.bank_account_number}
                        onToggle={() => toggleReveal("bank_account_number")}
                    />
                </div>
            </div>

            {/* Documents */}
            <div className="mt-12">
                <EmployeeDocuments
                    employeeId={employee.id}
                    employeeName={`${employee.first_name} ${employee.last_name}`}
                    employeeEmail={employee.work_email || employee.email}
                />
            </div>

            {/* Leave balance */}
            <div className="mt-12 bg-white border border-ink/10" data-testid="employee-leave-balance">
                <div className="p-10 pb-6 border-b border-ink/10 flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="overline">Time off · {balance?.year || new Date().getFullYear()}</div>
                        <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink flex items-center gap-3">
                            <CalendarClock className="w-5 h-5 text-klein" strokeWidth={1.5} /> Leave balance
                        </h2>
                    </div>
                    {balance && balance.entitlement > 0 && (
                        <div className="text-xs text-ink/50">
                            {balance.approved_days} of {balance.entitlement} days used
                            {balance.pending_days > 0 ? ` · ${balance.pending_days} pending` : ""}
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4">
                    <div className="p-6 border-r border-b border-ink/10" data-testid="balance-entitlement">
                        <div className="overline">Entitlement</div>
                        <div className="mt-2 font-display font-black text-4xl text-ink">{balance?.entitlement ?? 0}</div>
                        <div className="text-xs text-ink/50 mt-1">days / year</div>
                    </div>
                    <div className="p-6 border-r border-b border-ink/10" data-testid="balance-taken">
                        <div className="overline">Taken</div>
                        <div className="mt-2 font-display font-black text-4xl text-ink">{balance?.approved_days ?? 0}</div>
                        <div className="text-xs text-ink/50 mt-1">approved</div>
                    </div>
                    <div className="p-6 border-r border-b border-ink/10" data-testid="balance-pending">
                        <div className="overline">Pending</div>
                        <div className="mt-2 font-display font-black text-4xl text-ink">{balance?.pending_days ?? 0}</div>
                        <div className="text-xs text-ink/50 mt-1">awaiting decision</div>
                    </div>
                    <div className="p-6 border-b border-ink/10 bg-klein text-white" data-testid="balance-remaining">
                        <div className="overline !text-white/70">Remaining</div>
                        <div className="mt-2 font-display font-black text-4xl">{balance?.balance ?? 0}</div>
                        <div className="text-xs text-white/70 mt-1">days left</div>
                    </div>
                </div>
                {balance && balance.entitlement > 0 && (
                    <div className="px-10 py-5 border-b border-ink/10">
                        <div className="h-2 w-full bg-ink/5 relative">
                            <div
                                className="absolute left-0 top-0 h-full bg-klein"
                                style={{ width: `${Math.min((balance.approved_days / balance.entitlement) * 100, 100)}%` }}
                            />
                            <div
                                className="absolute top-0 h-full bg-klein/30"
                                style={{
                                    left: `${Math.min((balance.approved_days / balance.entitlement) * 100, 100)}%`,
                                    width: `${Math.min((balance.pending_days / balance.entitlement) * 100, 100 - Math.min((balance.approved_days / balance.entitlement) * 100, 100))}%`,
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Leave history */}
            <div className="mt-12 bg-white border border-ink/10 p-10" data-testid="employee-leaves">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="overline">Time off</div>
                        <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink">
                            Leave history
                        </h2>
                    </div>
                    <Link to="/leaves" className="text-sm text-ink/60 hover:text-klein">
                        Manage all →
                    </Link>
                </div>
                {leaves.length === 0 ? (
                    <div className="text-sm text-ink/40">No leave requests yet.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b-2 border-ink/20">
                                <th className="text-left overline py-3">Type</th>
                                <th className="text-left overline py-3">From</th>
                                <th className="text-left overline py-3">To</th>
                                <th className="text-left overline py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaves.map((l) => (
                                <tr key={l.id} className="border-b border-ink/10">
                                    <td className="py-3">{l.leave_type}</td>
                                    <td className="py-3">{l.start_date}</td>
                                    <td className="py-3">{l.end_date}</td>
                                    <td className="py-3 capitalize">{l.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Career timeline — merged salary + transfer events */}
            <CareerTimeline employee={employee} />

            {/* Recurring deductions — standing-order monthly deductions */}
            <RecurringDeductionsSection employeeId={id} canManage={hasRole(user, "admin", "hr")} />

            {/* Salary history */}
            {(employee.salary_history && employee.salary_history.length > 0) && (
                <div className="mt-12 bg-white border border-ink/10 p-10" data-testid="employee-salary-history">
                    <div className="overline">Compensation</div>
                    <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink">Salary history</h2>
                    <table className="w-full text-sm mt-6">
                        <thead>
                            <tr className="border-b-2 border-ink/20">
                                <th className="text-left overline py-3">Date</th>
                                <th className="text-right overline py-3">Previous</th>
                                <th className="text-right overline py-3">New</th>
                                <th className="text-right overline py-3">Δ</th>
                                <th className="text-left overline py-3">Reason</th>
                                <th className="text-left overline py-3">By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[...employee.salary_history].reverse().map((h, i) => {
                                const diff = (h.new_salary || 0) - (h.previous_salary || 0);
                                return (
                                    <tr key={`${h.date || ""}-${h.new_salary || 0}-${i}`} className="border-b border-ink/10">
                                        <td className="py-3">{h.date}</td>
                                        <td className="py-3 text-right tabular-nums">₦{Number(h.previous_salary || 0).toLocaleString()}</td>
                                        <td className="py-3 text-right tabular-nums font-medium">
                                            ₦{Number(h.new_salary || 0).toLocaleString()}
                                            {h.new_other_allowance !== undefined && h.new_other_allowance !== h.previous_other_allowance && (
                                                <div className="text-[11px] text-ink/50 font-normal">
                                                    Other allow.: ₦{Number(h.previous_other_allowance || 0).toLocaleString()} → ₦{Number(h.new_other_allowance || 0).toLocaleString()}
                                                </div>
                                            )}
                                        </td>
                                        <td className={`py-3 text-right tabular-nums ${diff >= 0 ? "text-green-700" : "text-red-700"}`}>
                                            {diff >= 0 ? "+" : ""}₦{Number(diff).toLocaleString()}
                                        </td>
                                        <td className="py-3 text-ink/70">{h.reason || "—"}</td>
                                        <td className="py-3 text-ink/50 text-xs">{h.by || "—"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {pcrOpen && <PayChangeRequestDialog employee={employee} onClose={() => setPcrOpen(false)} />}
            <EmployeeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={load}
                employee={employee}
                departments={departments}
            />

            <PassportPhotoUploader
                open={passportOpen}
                onClose={() => setPassportOpen(false)}
                employeeId={employee.id}
                currentPhotoUrl={employee.passport_photo_path}
                onSaved={load}
            />

            {incOpen && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setIncOpen(false)} data-testid="salary-increment-dialog">
                    <form onSubmit={onSubmitIncrement} onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-md">
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">Compensation update</div>
                                <h3 className="mt-1 font-display font-bold text-xl">Salary increment</h3>
                            </div>
                            <button type="button" onClick={() => setIncOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Current salary</label>
                                <div className="font-display font-bold text-2xl text-ink/80">₦{Number(employee.salary || 0).toLocaleString()}</div>
                            </div>
                            <div>
                                <label className="overline block mb-2">New salary (₦) *</label>
                                <MoneyInput
                                    required
                                    value={incForm.new_salary}
                                    onChange={(e) => setIncForm({ ...incForm, new_salary: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="increment-salary-input"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">New other allowance (₦ · non-taxable)</label>
                                <MoneyInput
                                    value={incForm.new_other_allowance ?? 0}
                                    onChange={(e) => setIncForm({ ...incForm, new_other_allowance: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="increment-other-allowance-input"
                                />
                                <p className="text-xs text-ink/50 mt-1">
                                    Currently ₦{Number(employee?.other_allowance || 0).toLocaleString()} — enter a higher or lower amount to increase or decrease it.
                                </p>
                            </div>
                            <div>
                                <label className="overline block mb-2">Effective date</label>
                                <input
                                    type="date"
                                    value={incForm.effective_date}
                                    onChange={(e) => setIncForm({ ...incForm, effective_date: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="increment-date-input"
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">Reason / note</label>
                                <input
                                    value={incForm.reason}
                                    onChange={(e) => setIncForm({ ...incForm, reason: e.target.value })}
                                    placeholder="Annual review, promotion, market adjustment…"
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="increment-reason-input"
                                />
                            </div>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setIncOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                            <button type="submit" disabled={incSaving} className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="increment-submit-button">
                                {incSaving ? "Recording…" : "Record increment"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {ffOpen && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setFfOpen(false)} data-testid="settlement-dialog">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-2xl">
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">Exit benefits</div>
                                <h3 className="mt-1 font-display font-bold text-xl">Full &amp; Final settlement</h3>
                            </div>
                            <button type="button" onClick={() => setFfOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="overline block mb-2">Type</label>
                                    <select
                                        value={ffKind}
                                        onChange={(e) => { setFfKind(e.target.value); setFfData(null); }}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm bg-transparent focus:border-klein focus:outline-none"
                                        data-testid="settlement-kind"
                                    >
                                        <option value="resignation">Resignation (1 month)</option>
                                        <option value="termination">Termination (3 months)</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="overline block mb-2">Effective date</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="date"
                                            value={ffEnd}
                                            onChange={(e) => { setFfEnd(e.target.value); setFfData(null); }}
                                            className="flex-1 border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                            data-testid="settlement-end-date"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => loadSettlement(ffKind, ffEnd)}
                                            disabled={ffLoading}
                                            className="px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                                            data-testid="settlement-calc"
                                        >
                                            {ffLoading ? "Calculating…" : "Calculate"}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {ffData && (
                                <div className="space-y-5" data-testid="settlement-result">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                                        <div className="bg-white p-4">
                                            <div className="overline">Service</div>
                                            <div className="mt-2 font-display font-bold text-xl tabular-nums">
                                                {ffData.service.years}y · {ffData.service.total_days}d
                                            </div>
                                        </div>
                                        <div className="bg-white p-4">
                                            <div className="overline">Gross / month</div>
                                            <div className="mt-2 font-display font-bold text-xl tabular-nums">
                                                ₦{Number(ffData.salary.gross).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="bg-white p-4">
                                            <div className="overline">Basic / day</div>
                                            <div className="mt-2 font-display font-bold text-xl tabular-nums">
                                                ₦{Number(ffData.salary.daily_basic).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className={`p-4 ${ffData.net_payable >= 0 ? "bg-green-700" : "bg-destructive"} text-white`}>
                                            <div className="overline !text-white/70">{ffData.net_payable >= 0 ? "Net payable" : "Net recoverable"}</div>
                                            <div className="mt-2 font-display font-bold text-xl tabular-nums">
                                                ₦{Math.abs(ffData.net_payable).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                                        <div className="bg-white p-5">
                                            <div className="overline mb-3 text-klein">Earnings</div>
                                            <ul className="text-sm space-y-2">
                                                <li className="flex justify-between"><span>{ffData.earnings.notice_months} month{ffData.earnings.notice_months !== 1 ? "s" : ""} in lieu of notice</span><span className="tabular-nums font-medium">₦{Number(ffData.earnings.notice_in_lieu).toLocaleString()}</span></li>
                                                <li className="flex justify-between"><span>Leave encashment ({ffData.earnings.leave_encashable_days} day{ffData.earnings.leave_encashable_days === 1 ? "" : "s"})</span><span className="tabular-nums font-medium">₦{Number(ffData.earnings.leave_encashment).toLocaleString()}</span></li>
                                                <li className="flex justify-between border-t border-ink/10 pt-2 mt-2"><span className="font-medium">Gross payable</span><span className="tabular-nums font-bold text-klein">₦{Number(ffData.earnings.gross_payable).toLocaleString()}</span></li>
                                            </ul>
                                            <p className="text-[11px] text-ink/50 mt-3 leading-relaxed">{ffData.earnings.leave_explanation}</p>
                                        </div>
                                        <div className="bg-white p-5">
                                            <div className="overline mb-3 text-destructive">Deductions</div>
                                            <ul className="text-sm space-y-2">
                                                <li className="flex justify-between"><span>Outstanding loan balance</span><span className="tabular-nums font-medium">₦{Number(ffData.deductions.loan_balance).toLocaleString()}</span></li>
                                                <li className="flex justify-between"><span>Outstanding advance balance</span><span className="tabular-nums font-medium">₦{Number(ffData.deductions.advance_balance).toLocaleString()}</span></li>
                                                <li className="flex justify-between border-t border-ink/10 pt-2 mt-2"><span className="font-medium">Total deductions</span><span className="tabular-nums font-bold text-destructive">₦{Number(ffData.deductions.total_deductions).toLocaleString()}</span></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-between gap-3">
                            <p className="text-xs text-ink/50">
                                {ffKind === "resignation" ? "1× gross" : "3× gross"} + leave encashment (≥1 yr, max 7 days) − outstanding loans/advances.
                            </p>
                            <div className="flex items-center gap-3 flex-wrap">
                                <button type="button" onClick={() => setFfOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Close</button>
                                <button
                                    type="button"
                                    onClick={downloadSettlementPdf}
                                    disabled={!ffData}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5 disabled:opacity-60"
                                    data-testid="settlement-pdf-button"
                                    title="Download PDF preview (no approval needed)"
                                >
                                    <FileDown className="w-4 h-4" /> Preview PDF
                                </button>
                                <input
                                    value={ffNote}
                                    onChange={(e) => setFfNote(e.target.value)}
                                    placeholder="Note for dept lead (optional)"
                                    className="hidden md:block flex-1 min-w-[220px] border border-ink/15 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                                />
                                <button
                                    type="button"
                                    onClick={submitSettlement}
                                    disabled={!ffData || ffSubmitting}
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                                    data-testid="settlement-initiate-button"
                                    title="Initiate workflow — Dept Lead will be emailed"
                                >
                                    <Receipt className="w-4 h-4" /> {ffSubmitting ? "Initiating…" : "Initiate F&F"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {letterOpen && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setLetterOpen(false)} data-testid="letter-dialog">
                    <div onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-xl">
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">Company letterhead</div>
                                <h3 className="mt-1 font-display font-bold text-xl">Generate letter</h3>
                            </div>
                            <button type="button" onClick={() => setLetterOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-5">
                            <div>
                                <label className="overline block mb-2">Template *</label>
                                <select
                                    value={letterTemplateId}
                                    onChange={(e) => setLetterTemplateId(e.target.value)}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm bg-transparent focus:border-klein focus:outline-none"
                                    data-testid="letter-template"
                                >
                                    {letterTemplates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="overline block mb-2">Letter date</label>
                                    <input
                                        type="date"
                                        value={letterDate}
                                        onChange={(e) => setLetterDate(e.target.value)}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="letter-date"
                                    />
                                </div>
                                <div>
                                    <label className="overline block mb-2">Override salary (optional)</label>
                                    <input
                                        type="text"
                                        value={letterSalary}
                                        onChange={(e) => setLetterSalary(e.target.value)}
                                        placeholder={`Defaults to ${Number(employee?.salary || 0).toLocaleString()}`}
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="letter-salary"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="overline block mb-2">Extra paragraph (optional)</label>
                                <textarea
                                    rows="4"
                                    value={letterExtra}
                                    onChange={(e) => setLetterExtra(e.target.value)}
                                    placeholder="Append a custom paragraph after the template body…"
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="letter-extra"
                                />
                            </div>
                            <p className="text-[11px] text-ink/50 leading-relaxed">
                                Letter uses your company logo, address, brand colour, and HR signature from <a href="/settings" className="text-klein underline">Settings</a>.
                                Manage templates from Settings → Letter templates.
                            </p>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setLetterOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Close</button>
                            <button
                                type="button"
                                onClick={downloadLetter}
                                disabled={letterDownloading || !letterTemplateId}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                                data-testid="letter-download-button"
                            >
                                <FileDown className="w-4 h-4" /> {letterDownloading ? "Generating…" : "Download PDF"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Face re-enrollment modal */}
            {faceOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="face-id-modal">
                    <div className="bg-white max-w-md w-full max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between p-5 border-b border-ink/10">
                            <div>
                                <div className="overline">Identity</div>
                                <h2 className="font-display font-bold text-xl mt-1">
                                    {faceStatus?.enrolled ? "Re-enroll face" : "Enroll face"} — {employee?.first_name}
                                </h2>
                            </div>
                            <button
                                onClick={() => setFaceOpen(false)}
                                className="p-2 hover:bg-ink/5"
                                data-testid="face-id-modal-close"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="p-5">
                            <p className="text-sm text-ink/60 mb-4">
                                Capture <strong>3 clear shots</strong> — front-facing, slight left turn, slight right turn. Replaces any existing samples on file.
                            </p>
                            <FaceCapture mode="enroll" requiredSamples={3} onComplete={onFaceEnrolled} />
                            {faceSaving && <div className="mt-3 text-xs text-ink/50">Saving…</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


// ---------- Career Timeline ----------
// Merges salary_history + transfer_history into a single chronological feed.
function CareerTimeline({ employee }) {
    const events = [];
    for (const h of (employee?.salary_history || [])) {
        if (!h || !h.date) continue;
        const diff = (h.new_salary || 0) - (h.previous_salary || 0);
        events.push({
            kind: "salary",
            date: h.date,
            previous: h.previous_salary,
            next: h.new_salary,
            diff,
            reason: h.reason || "",
            by: h.by || "",
        });
    }
    for (const t of (employee?.transfer_history || [])) {
        if (!t || !t.effective_date) continue;
        events.push({
            kind: "transfer",
            date: t.effective_date,
            previous: t.previous_company || "—",
            previous_legal: t.previous_company_legal_name || "",
            next: t.new_company || "—",
            next_legal: t.new_company_legal_name || "",
            by: t.transferred_by_name || t.transferred_by || "",
        });
    }
    // Joining date as the foundational event when present
    if (employee?.joining_date) {
        events.push({
            kind: "joined",
            date: employee.joining_date,
            company: employee.company || "",
        });
    }
    if (!events.length) return null;

    // Newest first
    events.sort((a, b) => (b.date || "").localeCompare(a.date || ""));

    return (
        <div className="mt-12 bg-white border border-ink/10 p-10" data-testid="employee-career-timeline">
            <div className="overline">Lifecycle</div>
            <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink">Career timeline</h2>
            <p className="text-sm text-ink/50 mt-1.5">Promotions, salary changes and inter-company transfers — newest first.</p>
            <ol className="mt-8 relative border-l-2 border-ink/10 ml-3 space-y-7">
                {events.map((e, i) => (
                    <li key={`${e.kind}-${e.date}-${i}`} className="pl-7" data-testid={`career-event-${e.kind}-${i}`}>
                        <span
                            className={`absolute -left-[11px] flex items-center justify-center w-5 h-5 rounded-full ${
                                e.kind === "salary"
                                    ? (e.diff >= 0 ? "bg-green-600 text-white" : "bg-red-600 text-white")
                                    : e.kind === "transfer"
                                        ? "bg-klein text-white"
                                        : "bg-ink text-white"
                            }`}
                        >
                            {e.kind === "salary" && <TrendingUp className="w-3 h-3" />}
                            {e.kind === "transfer" && <ArrowLeft className="w-3 h-3 rotate-180" />}
                            {e.kind === "joined" && <Briefcase className="w-3 h-3" />}
                        </span>
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.22em] text-ink/40 font-semibold">{e.date}</div>
                                <div className="font-display font-bold text-ink mt-1">
                                    {e.kind === "salary" && (
                                        <span data-testid={`career-salary-label-${i}`}>
                                            Salary {(e.diff || 0) >= 0 ? "increment" : "adjustment"}
                                        </span>
                                    )}
                                    {e.kind === "transfer" && <span data-testid={`career-transfer-label-${i}`}>Inter-company transfer</span>}
                                    {e.kind === "joined" && <span data-testid={`career-joined-label-${i}`}>Joined the company</span>}
                                </div>
                            </div>
                            <div className="text-right">
                                {e.kind === "salary" && (
                                    <div className="tabular-nums font-medium">
                                        <span className="text-ink/40">₦{Number(e.previous || 0).toLocaleString()}</span>
                                        <span className="mx-2 text-ink/30">→</span>
                                        <span className="text-ink">₦{Number(e.next || 0).toLocaleString()}</span>
                                        {(e.diff || 0) !== 0 && (
                                            <div className={`text-xs ${e.diff >= 0 ? "text-green-700" : "text-red-700"}`}>
                                                {e.diff >= 0 ? "+" : ""}₦{Number(e.diff).toLocaleString()}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {e.kind === "transfer" && (
                                    <div className="text-sm">
                                        <span className="text-ink/60">{e.previous_legal || e.previous}</span>
                                        <span className="mx-2 text-ink/30">→</span>
                                        <span className="text-ink font-medium">{e.next_legal || e.next}</span>
                                    </div>
                                )}
                                {e.kind === "joined" && e.company && (
                                    <div className="text-sm text-ink/70">{e.company}</div>
                                )}
                            </div>
                        </div>
                        {(e.reason || e.by) && (
                            <div className="mt-2 text-xs text-ink/50 flex items-center gap-4">
                                {e.reason && <span>{e.reason}</span>}
                                {e.by && <span className="ml-auto">by {e.by}</span>}
                            </div>
                        )}
                    </li>
                ))}
            </ol>
        </div>
    );
}

// ----- Recurring deductions inline section ---------------------------------

function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtNaira(n) {
    return "₦" + Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 0 });
}

function RecurringDeductionsSection({ employeeId, canManage }) {
    const { confirm } = useConfirm();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ label: "", amount: "", start_period: currentPeriod(), end_period: "", note: "", active: true });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/recurring-deductions", { params: { employee_id: employeeId } });
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [employeeId]);

    const monthly = items.filter((r) => r.active).reduce((s, r) => s + (Number(r.amount) || 0), 0);

    const resetForm = () => {
        setForm({ label: "", amount: "", start_period: currentPeriod(), end_period: "", note: "", active: true });
        setEditingId(null);
        setAdding(false);
    };

    const openEdit = (rd) => {
        setForm({
            label: rd.label || "",
            amount: String(rd.amount ?? ""),
            start_period: rd.start_period || currentPeriod(),
            end_period: rd.end_period || "",
            note: rd.note || "",
            active: !!rd.active,
        });
        setEditingId(rd.id);
        setAdding(true);
    };

    const onSave = async (e) => {
        e?.preventDefault?.();
        if (!form.label.trim()) { toast.error("Label is required"); return; }
        const amt = Number(form.amount);
        if (!amt || amt <= 0) { toast.error("Amount must be greater than zero"); return; }
        setSaving(true);
        try {
            if (editingId) {
                await api.put(`/recurring-deductions/${editingId}`, {
                    label: form.label.trim(),
                    amount: amt,
                    start_period: form.start_period || currentPeriod(),
                    end_period: form.end_period || null,
                    note: form.note.trim(),
                    active: !!form.active,
                });
                toast.success("Updated");
            } else {
                await api.post("/recurring-deductions", {
                    employee_id: employeeId,
                    label: form.label.trim(),
                    amount: amt,
                    start_period: form.start_period || currentPeriod(),
                    end_period: form.end_period || null,
                    note: form.note.trim(),
                    active: !!form.active,
                });
                toast.success("Added");
            }
            resetForm();
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const onToggle = async (rd) => {
        try {
            await api.put(`/recurring-deductions/${rd.id}`, { active: !rd.active });
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not update");
        }
    };

    const onDelete = async (rd) => {
        const ok = await confirm({
            title: "Delete recurring deduction?",
            description: `Remove "${rd.label}" (${fmtNaira(rd.amount)} / month)? Existing payroll runs already generated are not affected.`,
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/recurring-deductions/${rd.id}`);
            toast.success("Deleted");
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
        }
    };

    return (
        <div className="mt-12 bg-white border border-ink/10 p-10" data-testid="employee-recurring-deductions">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="overline flex items-center gap-2"><Repeat className="w-3 h-3" /> Payroll automation</div>
                    <h2 className="mt-2 font-display font-bold text-2xl tracking-tight text-ink">Recurring deductions</h2>
                    <p className="text-xs text-ink/50 mt-1 max-w-xl">
                        Standing-order deductions auto-applied to every monthly payroll run within the window. e.g. cooperative dues, union dues, rent recoveries.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="overline">Active · monthly</div>
                        <div className="mt-1 font-display font-bold text-xl text-klein tabular-nums" data-testid="employee-recurring-monthly">{fmtNaira(monthly)}</div>
                    </div>
                    {canManage && !adding && (
                        <button
                            type="button"
                            onClick={() => { setAdding(true); setEditingId(null); }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-[0.15em] border border-klein text-klein hover:bg-klein hover:text-white"
                            data-testid="employee-recurring-add"
                        >
                            <Plus className="w-3.5 h-3.5" /> Add
                        </button>
                    )}
                </div>
            </div>

            {/* Inline form */}
            {canManage && adding && (
                <form onSubmit={onSave} className="mt-6 bg-bone/30 border border-ink/10 p-5" data-testid="employee-recurring-form">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="overline block mb-2">Label *</label>
                            <input
                                value={form.label}
                                onChange={(e) => setForm({ ...form, label: e.target.value })}
                                placeholder="e.g. Cooperative dues"
                                className="w-full border border-ink/15 py-2 px-3 text-sm focus:border-klein focus:outline-none"
                                data-testid="employee-recurring-form-label"
                                required
                            />
                        </div>
                        <div>
                            <label className="overline block mb-2">Monthly amount (₦) *</label>
                            <MoneyInput
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                className="w-full border border-ink/15 py-2 px-3 text-sm focus:border-klein focus:outline-none"
                                data-testid="employee-recurring-form-amount"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="overline block mb-2">Start *</label>
                                <input
                                    type="month"
                                    value={form.start_period}
                                    onChange={(e) => setForm({ ...form, start_period: e.target.value })}
                                    className="w-full border border-ink/15 py-2 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="employee-recurring-form-start"
                                    required
                                />
                            </div>
                            <div>
                                <label className="overline block mb-2">End</label>
                                <input
                                    type="month"
                                    value={form.end_period}
                                    onChange={(e) => setForm({ ...form, end_period: e.target.value })}
                                    className="w-full border border-ink/15 py-2 px-3 text-sm focus:border-klein focus:outline-none"
                                    data-testid="employee-recurring-form-end"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline block mb-2">Note (optional)</label>
                            <input
                                value={form.note}
                                onChange={(e) => setForm({ ...form, note: e.target.value })}
                                className="w-full border border-ink/15 py-2 px-3 text-sm focus:border-klein focus:outline-none"
                                data-testid="employee-recurring-form-note"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                        <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-ink">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                className="w-4 h-4 accent-klein"
                                data-testid="employee-recurring-form-active"
                            />
                            Active
                        </label>
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={resetForm} className="px-4 py-2 text-sm text-ink/60 hover:text-ink">Cancel</button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="px-5 py-2 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60"
                                data-testid="employee-recurring-form-save"
                            >
                                {saving ? "Saving…" : editingId ? "Save" : "Add"}
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {/* Table */}
            <div className="mt-6">
                {loading ? (
                    <div className="text-sm text-ink/40 py-6 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-sm text-ink/40 py-8 text-center border border-dashed border-ink/15" data-testid="employee-recurring-empty">
                        No recurring deductions set up.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-ink/15 text-ink/50 text-[11px] uppercase tracking-[0.15em]">
                                    <th className="text-left py-2 px-3 font-medium">Label</th>
                                    <th className="text-right py-2 px-3 font-medium">Amount / month</th>
                                    <th className="text-left py-2 px-3 font-medium">Window</th>
                                    <th className="text-left py-2 px-3 font-medium">Status</th>
                                    {canManage && <th className="text-right py-2 px-3 font-medium">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((rd) => (
                                    <tr key={rd.id} className="border-b border-ink/5" data-testid={`employee-recurring-row-${rd.id}`}>
                                        <td className="py-3 px-3">
                                            <div className="text-ink">{rd.label}</div>
                                            {rd.note && <div className="text-[11px] text-ink/50">{rd.note}</div>}
                                        </td>
                                        <td className="py-3 px-3 text-right tabular-nums">{fmtNaira(rd.amount)}</td>
                                        <td className="py-3 px-3 text-ink/70 whitespace-nowrap">
                                            {rd.start_period || "—"} <span className="text-ink/30 mx-1">→</span> {rd.end_period || <span className="text-ink/40">open</span>}
                                        </td>
                                        <td className="py-3 px-3">
                                            {rd.active ? (
                                                <span className="inline-flex px-2 py-1 border border-green-200 bg-green-50 text-green-700 text-[11px] uppercase tracking-[0.15em]">Active</span>
                                            ) : (
                                                <span className="inline-flex px-2 py-1 border border-ink/10 bg-ink/5 text-ink/60 text-[11px] uppercase tracking-[0.15em]">Paused</span>
                                            )}
                                        </td>
                                        {canManage && (
                                            <td className="py-3 px-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => onToggle(rd)}
                                                        title={rd.active ? "Pause" : "Resume"}
                                                        className="p-1.5 text-ink/60 hover:text-ink hover:bg-ink/5"
                                                        data-testid={`employee-recurring-toggle-${rd.id}`}
                                                    >
                                                        {rd.active ? <PowerOff className="w-4 h-4" strokeWidth={1.5} /> : <Power className="w-4 h-4" strokeWidth={1.5} />}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => openEdit(rd)}
                                                        title="Edit"
                                                        className="p-1.5 text-ink/60 hover:text-ink hover:bg-ink/5"
                                                        data-testid={`employee-recurring-edit-${rd.id}`}
                                                    >
                                                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onDelete(rd)}
                                                        title="Delete"
                                                        className="p-1.5 text-ink/60 hover:text-destructive hover:bg-destructive/5"
                                                        data-testid={`employee-recurring-delete-${rd.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                    </button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
